import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Form, Input, Button, Modal, Select, DatePicker, Row, Col, InputNumber, Alert, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsersByOrganisation } from '../../redux/userOrganisationsSlice';
import dayjs from 'dayjs';
import { message } from 'antd';
import ConfirmModal from '../ConfirmModal';
import {
  canEditVacancyRequest,
  canEditSubmissionDate,
  canAssignVacancyRequest
} from '../../utils/vacancyRequestPermissions';

const workSchedules = [
  '6/1', '5/2', '4/4', '4/3', '4/2', '3/3', '2/2', '1/3', '1/2',
  'По выходным', 'Свободный', 'Другое'
];

const vacancyReasons = [
  'Новая вакансия',
  'Внутренний перевод',
  'Декрет',
  'Замена'
];

const { TextArea } = Input;
const { Option } = Select;

const EditModal = ({
  visible,
  onCancel,
  onSubmit,
  request,
  organisations = [],
  users = [],
}) => {
  const dispatch = useDispatch();
  const { list: organisationUsers } = useSelector(state => state.userOrganisations);
  const { user, role } = useSelector(state => state.auth);
  const userId = user?.id;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);

  // Получаем роль из Redux или localStorage (при перезагрузке страницы)
  const storedRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
  const effectiveRole = role || storedRole;

  // Проверяем права доступа на редактирование (только если request существует)
  const hasEditPermission = useMemo(() => {
    if (!request) return false;
    return canEditVacancyRequest(effectiveRole, userId, {
      authorId: request.author,
      approverHrId: request.approver_hr_id
    });
  }, [effectiveRole, userId, request]);

  // Проверяем права доступа на редактирование даты подачи
  const canEditDate = canEditSubmissionDate(effectiveRole);

  // Проверяем права доступа на назначение исполнителя
  const canAssign = useMemo(() => {
    if (!request) return false;
    return canAssignVacancyRequest(effectiveRole, userId, {
      approverHrId: request.approver_hr_id
    });
  }, [effectiveRole, userId, request]);

  const initialValuesRef = useRef({});

  // Используем Form.useWatch для отслеживания значений формы
  const businessTripsRequired = Form.useWatch('requires_travel', form);
  const relocationWillingness = Form.useWatch('requires_relocation', form);
  const communicationCompensation = Form.useWatch('compensate_phone', form);
  const carCompensation = Form.useWatch('compensate_car', form);
  const isMaterialResponsible = Form.useWatch('is_material_responsible', form);
  const driverLicenseRequired = Form.useWatch('requires_license', form);
  const ownCarRequired = Form.useWatch('requires_car', form);

  const handleCheckboxToggle = (fieldName) => {
    const currentValue = form.getFieldValue(fieldName) || false;
    form.setFieldsValue({ [fieldName]: !currentValue });
  };

  const getValidationRules = useCallback((fieldName, isDraft = false) => {
    const draftRequiredFields = {
      organisation: { required: true, message: 'Выберите компанию' },
      job_title: { required: true, message: 'Укажите название должности' }
    };

    if (isDraft) {
      return draftRequiredFields[fieldName] ? [draftRequiredFields[fieldName]] : [];
    }

    const requiredFields = {
      organisation: { required: true, message: 'Выберите компанию' },
      approver_hr: { required: true, message: 'Выберите согласующего HR' },
      submission_date: { required: true, message: 'Укажите дату подачи заявки' },
      department: { required: true, message: 'Выберите отдел' },
      city: { required: true, message: 'Укажите город' },
      planned_start_date: { required: true, message: 'Укажите планируемую дату выхода сотрудника на работу' },
      author: { required: true, message: 'Укажите ФИО автора заявки' },
      author_position: { required: true, message: 'Укажите должность автора заявки' },
      job_title: { required: true, message: 'Укажите название должности' },
      positions_count: { required: true, message: 'Укажите количество позиций' },
      rate: { required: true, message: 'Выберите ставку' },
      vacancy_reason: { required: true, message: 'Выберите причину возникновения вакансии' },
      supervisors: { required: true, message: 'Укажите начальника' },
      subordinates_count: { required: true, message: 'Укажите количество подчиненных' },
      is_material_responsible: { required: true, message: 'Укажите материально ответственное лицо' },
      tasks: { required: true, message: 'Опишите задачи' },
      expected_results: { required: true, message: 'Опишите ожидаемые результаты' },
      functional_responsibilities: { required: true, message: 'Опишите функциональные обязанности' },
      work_schedule: { required: true, message: 'Выберите график работы' },
      work_start_time: { required: true, message: 'Укажите время начала работы' },
      work_end_time: { required: true, message: 'Укажите время окончания работы' },
      business_trips_details: {
        required: false,
        message: 'Укажите детали командировок'
      },
      relocation_willingness: { required: true, message: 'Укажите готовность к переезду' },
      salary_structure: { required: true, message: 'Опишите структуру заработной платы' },
      probation_salary_min: { required: true, message: 'Укажите минимальную зарплату на период испытательного срока' },
      probation_salary_max: { required: true, message: 'Укажите максимальную зарплату на период испытательного срока' },
      regular_salary_min: { required: true, message: 'Укажите минимальную зарплату после испытательного срока' },
      regular_salary_max: { required: true, message: 'Укажите максимальную зарплату после испытательного срока' },
      work_address: { required: true, message: 'Укажите адрес рабочего места' }
    };

    return requiredFields[fieldName] ? [requiredFields[fieldName]] : [];
  }, []);

  const requiresComment = (status, isTemplate) => {
    if (isTemplate) {
      return false;
    }
    return ['pending', 'new', 'in_progress'].includes(status);
  };

  const canEdit = (status) => {
    if (['closed', 'archived'].includes(status)) {
      return false;
    }
    // Проверяем права доступа пользователя
    return hasEditPermission;
  };

  useEffect(() => {
    if (visible) {
      if (request) {
        const formValues = {
          submission_date: request.submission_date ? dayjs(request.submission_date) : dayjs(),
          organisation: request.organisation,
          department: request.department,
          city: request.city,
          planned_start_date: request.planned_start_date,
          author: request.author,
          author_position: request.author_position,
          assignee_id: request.assignee_id,
          job_title: request.job_title,
          positions_count: request.positions_count,
          rate: request.rate,
          vacancy_reason: request.vacancy_reason,
          supervisors: request.supervisors,
          subordinates_count: request.subordinates_count,
          is_material_responsible: request.is_material_responsible,
          tasks: request.tasks,
          expected_results: request.expected_results,
          functional_responsibilities: request.functional_responsibilities,
          reporting_details: request.reporting_details,
          work_schedule: request.work_schedule,
          work_start_time: request.work_start_time,
          work_end_time: request.work_end_time,
          field_vs_office: request.field_vs_office,
          requires_travel: request.requires_travel,
          travel_details: request.travel_details,
          requires_relocation: request.requires_relocation,
          salary_structure: request.salary_structure,
          compensate_phone: request.compensate_phone,
          compensate_car: request.compensate_car,
          workplace_address: request.workplace_address,
          gender: request.gender,
          age: request.age,
          marital_status: request.marital_status,
          requires_license: request.requires_license,
          license_categories: request.license_categories,
          requires_car: request.requires_car,
          education_levels: request.education_levels,
          computer_skills_level: request.computer_skills_level,
          skills_1c: request.skills_1c,
          software_skills: request.software_skills,
          language_skills: request.language_skills,
          experience_description: request.experience_description,
          personal_traits: request.personal_traits,
          professional_skills: request.professional_skills,
          preferred_companies: request.preferred_companies,
          unwanted_companies: request.unwanted_companies,
          other_conditions: request.other_conditions,
          comment: request.comment,
        };

        if (request.trial_salary) {
          const [probation_salary_min, probation_salary_max] = request.trial_salary.split(' - ');
          formValues.probation_salary_min = probation_salary_min ? parseFloat(probation_salary_min) : undefined;
          formValues.probation_salary_max = probation_salary_max ? parseFloat(probation_salary_max) : undefined;
        }
        if (request.post_trial_salary) {
          const [regular_salary_min, regular_salary_max] = request.post_trial_salary.split(' - ');
          formValues.regular_salary_min = regular_salary_min ? parseFloat(regular_salary_min) : undefined;
          formValues.regular_salary_max = regular_salary_max ? parseFloat(regular_salary_max) : undefined;
        }

        form.setFieldsValue(formValues);
        setSelectedOrganisation(request.organisation);
      } else {
        form.resetFields();
        setSelectedOrganisation(null);
      }
    }
  }, [visible, request, form]);

  useEffect(() => {
    if (selectedOrganisation) {
      const org = organisations.find(o => o.organisation_id === selectedOrganisation);
      setFilteredDepartments(org?.departments || []);
    } else {
      setFilteredDepartments([]);
    }
  }, [selectedOrganisation, organisations]);

  const checkForChanges = useCallback(() => {
    const currentValues = form.getFieldsValue(true);
    return JSON.stringify(currentValues) !== JSON.stringify(initialValuesRef.current);
  }, [form]);

  const handleFormChange = useCallback(() => {
    setHasUnsavedChanges(checkForChanges());
  }, [checkForChanges]);

  useEffect(() => {
    if (visible && request) {
      setTimeout(() => {
        initialValuesRef.current = form.getFieldsValue(true);
        setHasUnsavedChanges(false);
      }, 0);
    }
  }, [visible, request, form]);

  const handleModalCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirm(true);
    } else {
      onCancel();
    }
  }, [hasUnsavedChanges, onCancel]);

  const handleConfirmClose = useCallback((shouldClose) => {
    if (shouldClose) {
      onCancel();
    }
    setShowConfirm(false);
  }, [onCancel]);

  const handleCompanyChange = (orgId) => {
    setSelectedOrganisation(orgId);
    form.setFieldsValue({ department: undefined });

    if (orgId && !organisationUsers.length) {
      dispatch(fetchUsersByOrganisation(orgId));
    }
  };




  const scrollToFirstErrorField = (errorFields) => {
    if (errorFields && errorFields.length > 0) {
      const firstErrorField = errorFields[0];
      form.scrollToField(firstErrorField.name, {
        behavior: 'smooth',
        block: 'center',
      });
    }
  };


  const handleSubmit = async (isDraft = false) => {
    try {
      setLoading(true);

      let values;

      if (isDraft) {
        // Для сохранения черновика просто получаем значения без валидации
        values = form.getFieldsValue();
      } else {
        // Для создания заявки проверяем все обязательные поля
        try {
          // Обновляем правила валидации перед проверкой
          await form.validateFields([
            'organisation', 'submission_date', 'department', 'city',
            'planned_start_date', 'author', 'author_position', 'job_title',
            'positions_count', 'rate', 'vacancy_reason', 'supervisors', 'subordinates_count',
            'tasks', 'expected_results', 'functional_responsibilities',
            'work_schedule', 'work_start_time', 'work_end_time',
            'salary_structure', 'probation_salary_min',
            'probation_salary_max', 'regular_salary_min', 'regular_salary_max', 'workplace_address'
          ]);
          values = form.getFieldsValue();
        } catch (error) {
          if (error?.errorFields?.length > 0) {
            scrollToFirstErrorField(error.errorFields);
            message.error('Пожалуйста, заполните все обязательные поля');
            return;
          }
          throw error;
        }
      }

      if (!isDraft && !request?.is_template && requiresComment(request?.status, request?.is_template) && !values.comment?.trim()) {
        message.error('Обязательно укажите причину изменения заявки в комментарии');
        form.scrollToField('comment', {
          behavior: 'smooth',
          block: 'center',
        });
        return;
      }

      const data = {
        submission_date: values.submission_date ? dayjs(values.submission_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        organisation: values.organisation,
        department: values.department,
        city: values.city,
        planned_start_date: values.planned_start_date,
        author: values.author,
        author_position: values.author_position,
        assignee_id: values.assignee_id,
        is_template: isDraft ? request?.is_template : false,
        status: isDraft ? (request?.status || 'pending') : 'pending',
        job_title: values.job_title,
        positions_count: values.positions_count || 1,
        rate: values.rate ? String(values.rate) : '0.25',
        vacancy_reason: values.vacancy_reason,
        supervisors: values.supervisors,
        subordinates_count: values.subordinates_count ? String(values.subordinates_count) : '0',
        is_material_responsible: values.is_material_responsible || false,
        tasks: values.tasks,
        expected_results: values.expected_results,
        functional_responsibilities: values.functional_responsibilities,
        reporting_details: values.reporting_details,
        work_schedule: values.work_schedule,
        work_start_time: values.work_start_time,
        work_end_time: values.work_end_time,
        field_vs_office: values.field_vs_office,
        requires_travel: values.requires_travel || false,
        travel_details: values.travel_details,
        requires_relocation: values.requires_relocation || false,
        salary_structure: values.salary_structure,
        trial_salary: values.probation_salary_min !== undefined && values.probation_salary_max !== undefined
          ? `${values.probation_salary_min} - ${values.probation_salary_max}`
          : '',
        post_trial_salary: values.regular_salary_min !== undefined && values.regular_salary_max !== undefined
          ? `${values.regular_salary_min} - ${values.regular_salary_max}`
          : '',
        compensate_phone: values.compensate_phone || false,
        compensate_car: values.compensate_car || false,
        workplace_address: values.workplace_address,
        gender: values.gender,
        age: values.age,
        marital_status: values.marital_status || [],
        requires_license: values.requires_license || false,
        license_categories: values.license_categories || [],
        requires_car: values.requires_car || false,
        education_levels: values.education_levels || [],
        computer_skills_level: values.computer_skills_level,
        skills_1c: values.skills_1c,
        software_skills: (values.software_skills || []).length > 0
          ? (values.software_skills || [])
            .filter(sk => sk && sk.program && sk.program.trim() !== '')
            .map(sk => ({
              program: sk.program.trim(),
              level: sk.level || 'Не требуется'
            }))
          : undefined,
        language_skills: (values.language_skills || []).length > 0
          ? (values.language_skills || [])
            .filter(lang => lang && lang.language && lang.language.trim() !== '')
            .map(lang => ({
              language: lang.language.trim(),
              level: lang.level || 'Не требуется'
            }))
          : undefined,
        experience_description: values.experience_description,
        personal_traits: values.personal_traits,
        professional_skills: values.professional_skills,
        preferred_companies: values.preferred_companies,
        unwanted_companies: values.unwanted_companies,
        other_conditions: values.other_conditions,
        comment: values.comment,
      };

      await onSubmit(request.id, values.organisation, data);
      onCancel();
    } catch (error) {
      console.error('Ошибка при сохранении заявки:', error);
      if (error?.errorFields?.length > 0) {
        form.scrollToField(error.errorFields[0].name, {
          behavior: 'smooth',
          block: 'center',
        });
        message.error('Пожалуйста, заполните все обязательные поля');
      } else {
        message.error('Произошла ошибка при сохранении заявки');
      }
    } finally {
      setLoading(false);
    }
  };

  const isDraft = (request) => {
    return request?.is_template === true || request?.status === 'draft';
  };

  const footerButtons = [
    <Tooltip key="back" title="Закрыть модальное окно без сохранения изменений">
      <Button onClick={onCancel}>
        Закрыть
      </Button>
    </Tooltip>,
    <Tooltip key="submit" title="Сохранить изменения в заявке как черновик">
      <Button
        type="default"
        loading={loading}
        onClick={() => handleSubmit(true)}
        disabled={request && !canEdit(request.status)}
      >
        Сохранить
      </Button>
    </Tooltip>
  ];

  if (request && isDraft(request) && canEdit(request.status)) {
    footerButtons.push(
      <Tooltip key="submitForApproval" title="Создать заявку и отправить на согласование (проверка всех обязательных полей)">
        <Button
          type="primary"
          loading={loading}
          onClick={() => handleSubmit(false)}
          style={{ marginLeft: 8 }}
        >
          Создать заявку
        </Button>
      </Tooltip>
    );
  }

  return (
    <>
      <Modal
        title="Редактирование заявки на вакансию"
        open={visible}
        onOk={handleSubmit}
        onCancel={handleModalCancel}
        confirmLoading={loading}
        width={1000}
        footer={footerButtons}
      >
        <Form
          form={form}
          layout="vertical"
          onFieldsChange={handleFormChange}
          scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
        >
          {request && !canEdit(request.status) && (
            <Alert
              message="Редактирование недоступно!"
              description={
                !hasEditPermission
                  ? "У вас недостаточно прав для редактирования этой заявки."
                  : "Эта заявка закрыта или архивирована и не может быть отредактирована."
              }
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          {request && canEdit(request.status) && !request.is_template && requiresComment(request.status, request.is_template) && (
            <Alert
              message="Внимание!"
              description="Заявка находится в процессе согласования или уже согласована. При внесении изменений обязательно укажите причину в комментарии."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Основная информация
            </h3>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="job_title" label="Название должности" rules={getValidationRules('job_title', false)}>
                  <Input />
                </Form.Item>

                <Form.Item name="organisation" label="Компания" rules={getValidationRules('organisation', false)}>
                  <Select showSearch optionFilterProp="children" placeholder="Выберите компанию" onChange={handleCompanyChange}>
                    {organisations.map(o => (
                      <Option key={o.id} value={o.organisation_id}>{o.name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="department" label="Отдел" rules={getValidationRules('department', false)}>
                  <Select placeholder="Выберите отдел">
                    {filteredDepartments.map(d => (
                      <Option key={d.id} value={d.id}>{d.name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="city" label="Город" rules={getValidationRules('city', false)}>
                  <Input />
                </Form.Item>

                <Form.Item name="submission_date" label="Дата подачи заявки" rules={getValidationRules('submission_date', false)}>
                  <DatePicker
                    style={{ width: '100%' }}
                    defaultValue={dayjs()}
                    disabled={!canEditDate}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="author" label="Автор заявки (ФИО)" rules={getValidationRules('author', false)}>
                  <Select placeholder="Выберите автора">
                    {(selectedOrganisation && organisationUsers.length > 0 ? organisationUsers : users).map(u => (
                      <Option key={u.id} value={u.id}>{u.last_name} {u.first_name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="author_position" label="Автор заявки, должность" rules={getValidationRules('author_position', false)}>
                  <Input />
                </Form.Item>

                <Form.Item
                  name="assignee_id"
                  label="Исполнитель"
                  rules={getValidationRules('assignee_id', false)}
                  tooltip={!canAssign ? "У вас недостаточно прав для назначения исполнителя" : undefined}
                >
                  <Select
                    placeholder="Выберите исполнителя"
                    disabled={!canAssign}
                  >
                    {(selectedOrganisation && organisationUsers.length > 0 ? organisationUsers : users).map(u => (
                      <Option key={u.id} value={u.id}>{u.last_name} {u.first_name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="planned_start_date" label="Планируемая дата выхода сотрудника на работу" rules={getValidationRules('planned_start_date', false)}>
                  <Input placeholder="Можно указать дату, месяц или период (например: 01.09.2023, сентябрь 2023, 3-й квартал 2023)" />
                </Form.Item>

                <Form.Item name="positions_count" label="Количество позиций" rules={getValidationRules('positions_count', false)}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Информация о вакансии */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Информация о вакансии
            </h3>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="rate" label="Ставка" rules={getValidationRules('rate', false)}>
                  <Select placeholder="Выберите ставку">
                    {['0.25', '0.5', '0.75', '1', '1.25', '1.5', '1.75', '2'].map(value => (
                      <Option key={value} value={parseFloat(value)}>
                        {value}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="supervisors" label="Начальник" rules={getValidationRules('supervisors', false)}>
                  <Input />
                </Form.Item>

                <Form.Item label="Материально ответственное лицо">
                  <Button
                    type={isMaterialResponsible ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('is_material_responsible')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: isMaterialResponsible ? 'bold' : 'normal'
                    }}
                  >
                    {isMaterialResponsible ? '✓ Материально ответственное лицо' : 'Материально ответственное лицо'}
                  </Button>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="vacancy_reason" label="Причина возникновения вакансии" rules={getValidationRules('vacancy_reason', false)}>
                  <Select>
                    {vacancyReasons.map(reason => (
                      <Option key={reason} value={reason}>{reason}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="subordinates_count" label="Подчиненные (количество)" rules={getValidationRules('subordinates_count', false)}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item name="is_material_responsible" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Должностные обязанности */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Должностные обязанности
            </h3>

            <Form.Item name="tasks" label="Задачи" rules={getValidationRules('tasks', false)}>
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="functional_responsibilities" label="Функциональные обязанности" rules={getValidationRules('functional_responsibilities', false)}>
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="reporting_details" label="Отчетность о проделанной работе" rules={getValidationRules('reporting_details', false)}>
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="expected_results" label="Ожидаемые результаты" rules={getValidationRules('expected_results', false)}>
              <TextArea rows={3} />
            </Form.Item>
          </div>

          {/* Условия работы */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Условия работы
            </h3>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="work_schedule" label="График работы" rules={getValidationRules('work_schedule', false)}>
                  <Select placeholder="Выберите график работы">
                    {workSchedules.map(schedule => (
                      <Option key={schedule} value={schedule}>{schedule}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="work_start_time" label="Начало работы" rules={getValidationRules('work_start_time', false)}>
                  <Input placeholder="Например: 9:00 или 'Гибкое'" />
                </Form.Item>

                <Form.Item name="requires_relocation" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>

                <Form.Item label="Готовность к переезду">
                  <Button
                    type={relocationWillingness ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('requires_relocation')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: relocationWillingness ? 'bold' : 'normal'
                    }}
                  >
                    {relocationWillingness ? '✓ Готовность к переезду' : 'Готовность к переезду'}
                  </Button>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="field_vs_office" label="Соотношение работы: дом/офис" rules={getValidationRules('field_vs_office', false)}>
                  <Input placeholder="Например: 70% офис, 30% дом" />
                </Form.Item>

                <Form.Item name="work_end_time" label="Окончание работы" rules={getValidationRules('work_end_time', false)}>
                  <Input placeholder="Например: 18:00 или 'Гибкое'" />
                </Form.Item>

                <Form.Item name="requires_travel" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>

                <Form.Item label="Командировки">
                  <Button
                    type={businessTripsRequired ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('requires_travel')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: businessTripsRequired ? 'bold' : 'normal'
                    }}
                  >
                    {businessTripsRequired ? '✓ Командировки' : 'Командировки'}
                  </Button>
                </Form.Item>

                {businessTripsRequired && (
                  <Form.Item name="travel_details" label="Частота и длительность командировок" rules={getValidationRules('travel_details', false)}>
                    <Input placeholder="Например: 2 раза в месяц, 3-5 дней" />
                  </Form.Item>
                )}
              </Col>
            </Row>
          </div>

          {/* Зарплата */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Зарплата
            </h3>

            <Form.Item name="salary_structure" label="Заработная плата (структура)" rules={getValidationRules('salary_structure', false)}>
              <TextArea rows={2} placeholder="Опишите как формируется заработная плата" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Зарплата на период испытательного срока (BYN)"
                  required
                  validateStatus={form.getFieldError('probation_salary_min')?.length > 0 || form.getFieldError('probation_salary_max')?.length > 0 ? 'error' : ''}
                  help={form.getFieldError('probation_salary_min')?.[0] || form.getFieldError('probation_salary_max')?.[0]}
                >
                  <Input.Group compact>
                    <Form.Item name="probation_salary_min" noStyle rules={getValidationRules('probation_salary_min', false)}>
                      <InputNumber
                        min={0}
                        placeholder="От"
                        style={{ width: '50%' }}
                        parser={value => value.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                    <Form.Item name="probation_salary_max" noStyle rules={getValidationRules('probation_salary_max', false)}>
                      <InputNumber
                        min={0}
                        placeholder="До"
                        style={{ width: '50%' }}
                        parser={value => value.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Зарплата после испытательного срока (BYN)"
                  required
                  validateStatus={form.getFieldError('regular_salary_min')?.length > 0 || form.getFieldError('regular_salary_max')?.length > 0 ? 'error' : ''}
                  help={form.getFieldError('regular_salary_min')?.[0] || form.getFieldError('regular_salary_max')?.[0]}
                >
                  <Input.Group compact>
                    <Form.Item name="regular_salary_min" noStyle rules={getValidationRules('regular_salary_min', false)}>
                      <InputNumber
                        min={0}
                        placeholder="От"
                        style={{ width: '50%' }}
                        parser={value => value.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                    <Form.Item name="regular_salary_max" noStyle rules={getValidationRules('regular_salary_max', false)}>
                      <InputNumber
                        min={0}
                        placeholder="До"
                        style={{ width: '50%' }}
                        parser={value => value.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="compensate_phone" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item label="Компенсация затрат: связь">
                  <Button
                    type={communicationCompensation ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('compensate_phone')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: communicationCompensation ? 'bold' : 'normal'
                    }}
                  >
                    {communicationCompensation ? '✓ Компенсация затрат: связь' : 'Компенсация затрат: связь'}
                  </Button>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="compensate_car" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item label="Компенсация затрат: личный автомобиль">
                  <Button
                    type={carCompensation ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('compensate_car')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: carCompensation ? 'bold' : 'normal'
                    }}
                  >
                    {carCompensation ? '✓ Компенсация затрат: личный автомобиль' : 'Компенсация затрат: личный автомобиль'}
                  </Button>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="workplace_address" label="Адрес рабочего места" rules={getValidationRules('workplace_address', false)}>
              <Input placeholder="Полный адрес рабочего места" />
            </Form.Item>
          </div>

          {/* Требования к кандидату */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Требования к кандидату
            </h3>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="gender" label="Пол" rules={getValidationRules('gender', false)}>
                  <Select options={[
                    { value: 'Мужской', label: 'Мужской' },
                    { value: 'Женский', label: 'Женский' },
                    { value: 'Не имеет значения', label: 'Не имеет значения' },
                  ]} />
                </Form.Item>

                <Form.Item name="marital_status" label="Семейное положение" rules={getValidationRules('marital_status', false)}>
                  <Select
                    mode="multiple"
                    options={[
                      { value: 'Холост/Не замужем', label: 'Холост/Не замужем' },
                      { value: 'Женат/Замужем', label: 'Женат/Замужем' },
                      { value: 'Разведен(а)', label: 'Разведен(а)' },
                      { value: 'Вдовец/Вдова', label: 'Вдовец/Вдова' },
                      { value: 'Не имеет значения', label: 'Не имеет значения' },
                    ]}
                  />
                </Form.Item>

                <Form.Item label="Наличие водительского удостоверения">
                  <Button
                    type={driverLicenseRequired ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('requires_license')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: driverLicenseRequired ? 'bold' : 'normal'
                    }}
                  >
                    {driverLicenseRequired ? '✓ Наличие водительского удостоверения' : 'Наличие водительского удостоверения'}
                  </Button>
                </Form.Item>

                {driverLicenseRequired && (
                  <Form.Item name="license_categories" label="Категории водительских прав">
                    <Select
                      mode="multiple"
                      options={[
                        'АМ', 'А', 'А1', 'В', 'С', 'D', 'ВЕ', 'СЕ', 'DE', 'F', 'I'
                      ].map(cat => ({ value: cat, label: cat }))}
                    />
                  </Form.Item>
                )}
              </Col>

              <Col span={12}>
                <Form.Item name="age" label="Возраст" rules={getValidationRules('age', false)}>
                  <Input placeholder="Укажите возраст или диапазон" />
                </Form.Item>

                <Form.Item name="education_levels" label="Образование" rules={getValidationRules('education_levels', false)}>
                  <Select
                    mode="multiple"
                    options={[
                      'Базовое',
                      'Среднее',
                      'Профессионально-техническое',
                      'Среднее специальное',
                      'Высшее'
                    ].map(ed => ({ value: ed, label: ed }))}
                  />
                </Form.Item>

                <Form.Item name="requires_license" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>

                <Form.Item name="requires_car" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>

                <Form.Item label="Наличие личного автомобиля">
                  <Button
                    type={ownCarRequired ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('requires_car')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: ownCarRequired ? 'bold' : 'normal'
                    }}
                  >
                    {ownCarRequired ? '✓ Наличие личного автомобиля' : 'Наличие личного автомобиля'}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Навыки и опыт */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Навыки и опыт
            </h3>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="computer_skills_level" label="Уровень владения ПК (Word, Excel и др.)" rules={getValidationRules('computer_skills_level', false)}>
                  <Select
                    options={[
                      'Не требуется',
                      'Базовый',
                      'Уверенный',
                      'Продвинутый'
                    ].map(level => ({ value: level, label: level }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="skills_1c" label="Навыки в работе с 1С" rules={getValidationRules('skills_1c', false)}>
                  <Input.TextArea rows={1} />
                </Form.Item>
              </Col>
            </Row>

            <Form.List name="software_skills">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={8} key={key}>
                      <Col span={12}>
                        <Form.Item {...restField} name={[name, 'program']} label="Программа">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item {...restField} name={[name, 'level']} label="Уровень владения">
                          <Select options={[
                            'Базовый',
                            'Уверенный',
                            'Продвинутый'
                          ].map(l => ({ value: l, label: l }))} />
                        </Form.Item>
                      </Col>
                      <Col span={2} style={{ marginTop: '6px', display: 'flex', alignItems: 'center' }}>
                        <Button danger onClick={() => remove(name)}>Удалить</Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item label="Знание других специализированных программ">
                    <Button type="dashed" onClick={() => add()}>Добавить программу</Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.List name="language_skills">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={8} key={key}>
                      <Col span={12}>
                        <Form.Item {...restField} name={[name, 'language']} label="Язык">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item {...restField} name={[name, 'level']} label="Уровень владения">
                          <Select options={[
                            'Базовый',
                            'Уверенный',
                            'Продвинутый'
                          ].map(l => ({ value: l, label: l }))} />
                        </Form.Item>
                      </Col>
                      <Col span={2} style={{ marginTop: '6px', display: 'flex', alignItems: 'center' }}>
                        <Button danger onClick={() => remove(name)}>Удалить</Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item label="Знание иностранных языков">
                    <Button type="dashed" onClick={() => add()}>Добавить язык</Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.Item name="experience_description" label="Опыт работы (область, продолжительность)" rules={getValidationRules('experience_description', false)}>
              <TextArea rows={2} />
            </Form.Item>
          </div>

          {/* Личные и профессиональные качества */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Личные и профессиональные качества
            </h3>

            <Form.Item name="personal_traits" label="Личные качества кандидатов" rules={getValidationRules('personal_traits', false)}>
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="professional_skills" label="Профессиональные навыки кандидатов" rules={getValidationRules('professional_skills', false)}>
              <TextArea rows={3} />
            </Form.Item>
          </div>

          {/* Дополнительные требования */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Дополнительные требования
            </h3>

            <Form.Item name="preferred_companies" label="Компании, из которых предпочтительно рассматривать кандидатов" rules={getValidationRules('preferred_companies', false)}>
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item name="unwanted_companies" label="Компании, из которых нежелательно рассматривать кандидатов" rules={getValidationRules('unwanted_companies', false)}>
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item name="other_conditions" label="Другие условия" rules={getValidationRules('other_conditions', false)}>
              <TextArea rows={3} />
            </Form.Item>
          </div>

          {!(request?.is_template) && (
            <Form.Item
              name="comment"
              label="Комментарий"
              rules={[
                {
                  required: request && requiresComment(request.status, request.is_template),
                  message: 'Обязательно укажите причину изменения заявки'
                }
              ]}
            >
              <TextArea
                rows={3}
                placeholder={
                  request && requiresComment(request.status, request.is_template)
                    ? "Обязательно укажите причину изменения заявки..."
                    : "Дополнительная информация..."
                }
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <ConfirmModal
        visible={showConfirm}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowConfirm(false)}
        title="Подтверждение закрытия"
        message="Данные не будут сохранены. Продолжить?"
        okText="Продолжить"
        cancelText="Отмена"
        type="warning"
      />
    </>
  );
};

export default EditModal;